import { useEffect, useRef, useState } from "react";

interface AnimatedTitleProps {
  /** 英字テキスト（1文字ずつアニメーション） */
  text: string;
  /** アウトライン文字（上に重なる） */
  outlineText?: string;
  /** 下に重なる塗り文字 */
  filledText?: string;
  /** 重ね文字モード */
  stacked?: boolean;
  /** 遅延秒数 */
  delay?: number;
  /** 追加のクラス名 */
  className?: string;
  /** タイトル上部に赤いラインを追加 */
  showAccentLine?: boolean;
  /** タイトル上部にラベルタグを追加 */
  labelTag?: string;
  /** 下端に赤いラインを追加 */
  showBottomLine?: boolean;
  /** フォントサイズ（デフォルト: clamp(56px, 8vw, 96px)） */
  fontSize?: string;
}

/**
 * Goals.co.jp 風の1文字ずつフェードインするセクションタイトル。
 * - stacked=true: アウトライン文字（上）+ 塗り文字（下にオフセット重ね）
 * - stacked=false: 単一テキストを1文字ずつアニメーション
 */
export default function AnimatedTitle({
  text,
  outlineText,
  filledText,
  stacked = false,
  delay = 0,
  className = "",
  showAccentLine = true,
  labelTag,
  showBottomLine = true,
  fontSize = "clamp(56px, 8vw, 96px)",
}: AnimatedTitleProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setRevealed(true);
          obs.disconnect();
        }
      },
      { threshold: 0.05, rootMargin: "0px 0px -50px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // 文字を1文字ずつspanでラップ
  const charSpans = (str: string, baseDelay: number) =>
    str.split("").map((char, i) => (
      <span
        key={i}
        style={{
          display: "inline-block",
          animationDelay: revealed ? `${baseDelay + delay + i * 0.03}s` : "0s",
        }}
      >
        {char === " " ? "\u00A0" : char}
      </span>
    ));

  return (
    <div ref={ref} className={className}>
      {/* ラベルタグ */}
      {labelTag && (
        <div
          className="section-label-tag mb-5"
          style={{
            opacity: revealed ? 1 : 0,
            transform: revealed ? "translateY(0)" : "translateY(10px)",
            transition: `opacity 0.6s ease ${delay}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
          }}
        >
          {labelTag}
        </div>
      )}

      {/* 上部アクセントライン */}
      {showAccentLine && (
        <div
          className="section-line-anim mb-6"
          style={{
            width: "40px",
            height: "2px",
            background: "#c00000",
          }}
        >
          <div
            style={{
              width: "100%",
              height: "100%",
              background: "#c00000",
              transform: revealed ? "scaleX(1)" : "scaleX(0)",
              transformOrigin: "left center",
              transition: `transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
            }}
          />
        </div>
      )}

      {/* タイトル */}
      {stacked && outlineText && filledText ? (
        <div className="relative select-none">
          {/* アウトライン層 */}
          <div
            className={`section-char-wrap ${revealed ? "revealed" : ""}`}
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "-0.01em",
              color: "transparent",
              WebkitTextStroke: "1.5px #111111",
            }}
          >
            {charSpans(outlineText, 0)}
          </div>
          {/* 塗り層（下にオフセット） */}
          <div
            className={`section-char-wrap ${revealed ? "revealed" : ""}`}
            style={{
              fontFamily: "'Barlow', sans-serif",
              fontSize,
              fontWeight: 700,
              lineHeight: 1,
              letterSpacing: "-0.01em",
              color: "#111111",
              marginTop: "-0.42em",
              marginLeft: "0.35em",
            }}
          >
            {charSpans(filledText, 0.15)}
          </div>
        </div>
      ) : (
        <div
          className={`section-char-wrap ${revealed ? "revealed" : ""}`}
          style={{
            fontFamily: "'Barlow', sans-serif",
            fontSize,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: "-0.01em",
            color: "#111111",
          }}
        >
          {charSpans(text, 0)}
        </div>
      )}

      {/* 下部ライン */}
      {showBottomLine && (
        <div
          className="mt-6 md:mt-8"
          style={{
            height: "1px",
            background: "rgba(0,0,0,0.08)",
            transform: revealed ? "scaleX(1)" : "scaleX(0)",
            transformOrigin: "left center",
            transition: `transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay + 0.3}s`,
          }}
        />
      )}
    </div>
  );
}